import { useCallback, useMemo, useState } from 'react';
import { useProperties, useProperty } from './properties';
import { useFilesMetadata } from '../files';
import { createQuery } from '../../../utils/api/query-builder.util';
import { getPrimaryImageFileId } from '../../../utils/catalog/property-media.util';

/**
 * Load the selected thumbnail immediately and search thumbnails on first opening.
 * @param propertyId - Selected property, including one outside the search results.
 * @param take - Maximum number of properties requested per search.
 * @returns Property data, ready-to-render images and search/open actions.
 */
export function usePropertySelectorData(propertyId: number | null, take = 20) {
  const [enabled, setEnabled] = useState(false);
  const [search, setSearch] = useState('');
  const { property: selectedProperty, loading: loadingSelectedProperty } =
    useProperty(propertyId ?? -1, propertyId !== null);
  const query = useMemo(() => {
    const builder = createQuery().take(take).orderByDesc('createdAt');
    builder.equals('isActive', true);
    if (search.trim()) builder.search(search.trim());
    return builder.build();
  }, [search, take]);
  const { properties: searchedProperties, loading: loadingProperties } =
    useProperties({ ...query, enabled });
  const onOpenChange = useCallback((open: boolean) => {
    if (open) setEnabled(true);
  }, []);
  const imageFileByProperty = new Map(
    [selectedProperty, ...(enabled ? searchedProperties : [])].flatMap(
      (property) =>
        property ? [[String(property.id), getPrimaryImageFileId(property)]] : []
    )
  );
  const { files, loading: loadingImages } = useFilesMetadata(
    [...imageFileByProperty.values()].filter((id): id is string => Boolean(id))
  );
  const imageUrls = new Map(files.map((file) => [file.id, file.url]));
  const images = new Map(
    [...imageFileByProperty].map(([id, fileId]) => {
      const url = fileId ? imageUrls.get(fileId) ?? null : null;
      return [id, { url, loading: Boolean(fileId) && !url && loadingImages }];
    })
  );

  return {
    selectedProperty,
    searchedProperties,
    images,
    loading: loadingProperties || loadingSelectedProperty,
    onOpenChange,
    setSearch,
  };
}
